import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {Alert, Button, Col, Row, Space} from "antd";
import {StyledServiceButtonWrapper} from "../../../../styles/onboarding/onboarding";
import {useNavigate} from "react-router-dom";
import {
  editProductBasicFormStepFormValidationSchema,
  EditProductBasicFormStepFormValues,
} from "../schema-validation";
import {EditProductFormValues} from "../types";
import {useEditProductHook} from "../hooks/edit-product";
import {useGetProductContext} from "../context/get-product";
import {EditProductOfferingForm} from "../../../../components/offering-form/products";
import {ServiceFormLayout} from "../../../../calls/useCases/createService/service-form-layout";
import {OfferingDeleteButton} from "../../../../components/offering-form/general/delete-button";
import {useDeleteProductHook} from "../hooks/delete-product";
import {useEffect, useMemo} from "react";
import {
  useCoverImagesWithFallback,
  mapDomainToEmbedded,
} from "../../../../features/media";
import {usePricingCurrencies} from "@/features/currencies";
import {IS_PWYW_ENABLED} from "../../../../services/config";
import {EditProductAddOnGate} from "./add-on-gate";
import {toSavedEditProductFormValues} from "../saved-form-values";

export function EditProductBasicForm() {
  const navigate = useNavigate();
  const {
    state: {productData},
  } = useGetProductContext();
  const {editProduct, isEditingProduct} = useEditProductHook();
  const {deleteProduct} = useDeleteProductHook(productData.product.id);
  const pricingCurrencies = usePricingCurrencies();

  // Fetch cover images via v2 with fallback to embedded data
  const {coverImages} = useCoverImagesWithFallback({
    offeringType: "product",
    offeringId: productData.product.id,
    embedded: productData.product.cover_images,
  });

  // Memoize to prevent infinite loops in useEffect
  const coverImagesForForm = useMemo(
    () => mapDomainToEmbedded(coverImages),
    [coverImages]
  );

  const formMethods = useForm<EditProductBasicFormStepFormValues>({
    resolver: yupResolver(editProductBasicFormStepFormValidationSchema),
    mode: "onSubmit",
    defaultValues: {
      ...toSavedEditProductFormValues(productData, IS_PWYW_ENABLED),
      cover_images: productData.product.cover_images,
    },
  });

  useEffect(() => {
    formMethods.setValue("cover_images", coverImagesForForm);
    if (formMethods.watch("cover_images").length > 0) {
      formMethods.clearErrors("cover_images");
    } else {
      formMethods.trigger("cover_images");
    }
  }, [formMethods, coverImagesForForm]);

  // Add default currency when switching from free to paid
  const watchedPriceType = formMethods.watch("priceType");
  const watchedCurrencies = formMethods.watch("currencies");
  useEffect(() => {
    if (
      watchedPriceType === "paid" &&
      watchedCurrencies.length === 0 &&
      pricingCurrencies.formCurrencies.length > 0
    ) {
      formMethods.setValue("currencies", [
        {
          callPrice: 100,
          callPriceCurrency: pricingCurrencies.formCurrencies[0],
        },
      ]);
    }
  }, [
    watchedPriceType,
    watchedCurrencies.length,
    pricingCurrencies.formCurrencies,
    formMethods,
  ]);

  // console.log({watx: formMethods.watch()});

  const isFormError = Object.keys(formMethods.formState.errors).length > 0;
  const isGeneralInformationError =
    formMethods.formState.errors.name ||
    formMethods.formState.errors.description ||
    formMethods.formState.errors.coverImage;
  const isCoverImagesError = formMethods.formState.errors.cover_images;
  const isPricingError =
    formMethods.formState.errors.priceType || formMethods.formState.errors.currencies;
  const isSettingsError =
    formMethods.formState.errors.hideFromProfile ||
    formMethods.formState.errors.isUrlRedirect ||
    formMethods.formState.errors.redirectAfterPurchase ||
    formMethods.formState.errors.telegramGroupId ||
    formMethods.formState.errors.requestCustomerPhoneNumber ||
    formMethods.formState.errors.limitUnitsPerTransaction ||
    formMethods.formState.errors.purchaseLimit ||
    formMethods.formState.errors.url_redirect;

  // console.log({editformData: productData});

  function onSubmit(values: EditProductBasicFormStepFormValues) {
    // console.log({values});
    const payload: EditProductFormValues = {
      bookingQuestions: productData.product.bookingQuestions
        ? productData.product.bookingQuestions.map((q) => {
            if (
              q.answer_type === "checkbox" ||
              q.answer_type === "dropdown" ||
              q.answer_type === "radio-button"
            ) {
              return {
                question: q.question,
                required: q.required,
                answerType: q.answer_type,
                options: q.options.map((o) => ({
                  optionLabel: o,
                })),
              };
            } else {
              return {
                question: q.question,
                required: q.required,
                answerType: q.answer_type,
                options: [
                  {
                    optionLabel: "",
                  },
                ],
              };
            }
          })
        : [],
      coverImage: values.coverImage,
      currencies: values.currencies,
      description: values.description,
      hideFromProfile: values.hideFromProfile,
      id: productData.product.id,
      isUrlRedirect: values.isUrlRedirect,
      name: values.name,
      priceType: values.priceType,
      requestCustomerPhoneNumber: values.requestCustomerPhoneNumber,
      limitUnitsPerTransaction: values.limitUnitsPerTransaction,
      purchaseLimit: values.purchaseLimit,
      url_redirect: values.url_redirect,
      redirectAfterPurchase: values.redirectAfterPurchase,
      telegramGroupId: values.telegramGroupId,
      enable_currency_conversion: true,
      selected_currencies: values.selected_currencies || [],
      // variants: values.variants,
    };
    editProduct(payload);
  }

  // console.log({editwatch: formMethods.watch(), editerrors: formMethods.formState.errors});
  return (
    <ServiceFormLayout>
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(onSubmit)} autoComplete="off">
          <Space direction="vertical" size={16} style={{display: "flex"}}>
            <EditProductAddOnGate productId={productData.product.id}>
              <EditProductOfferingForm
                isGeneralInformationError={!!isGeneralInformationError}
                isPricingError={!!isPricingError}
                isSettingsError={!!isSettingsError}
                isCoverImagesError={!!isCoverImagesError}
              />
            </EditProductAddOnGate>
            {isFormError && (
              <Alert
                message="Please fill in the required fields above, outlined in red"
                type="warning"
              />
            )}

            <Row gutter={[0, 16]}>
              <Col span={24} sm={12}>
                <StyledServiceButtonWrapper>
                  <Button
                    className="secondary-btn"
                    onClick={() => {
                      navigate("/calls");
                    }}
                    block
                    size="small"
                    type="default"
                  >
                    Back
                  </Button>
                  <Button
                    block
                    type="primary"
                    htmlType={"submit"}
                    size="small"
                    disabled={
                      Object.keys(formMethods.formState.errors).length > 0 ||
                      Object.values(formMethods.watch()).length < 1
                    }
                    loading={isEditingProduct}
                  >
                    Update product
                  </Button>
                  <OfferingDeleteButton
                    cancelText="Exit"
                    content="Are you sure you want to delete this product? This action cannot be undone."
                    okText="Delete product"
                    onOk={deleteProduct}
                    title="Confirm product deletion"
                  />
                </StyledServiceButtonWrapper>
              </Col>
            </Row>
          </Space>
        </form>
      </FormProvider>
    </ServiceFormLayout>
  );
}
